import React, { createContext, useState, useContext, useEffect } from "react";

// Define shape of user data
const defaultUser = {
  id: null,
  name: "",
  email: "",
  role: "",
  token: "",
  isVerified: false,
};

const UserContext = createContext({
  user: defaultUser,
  isLoading: true,
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
  updateUser: () => {},
});

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(defaultUser);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from localStorage on initial render
  useEffect(() => {
    const loadUser = async () => {
      try {
        const storedUser = localStorage.getItem("myToken");
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);

          // Recalculate verification status based on role
          const updatedUser = {
            ...parsedUser,
            isVerified:
              parsedUser.role === "admin" || parsedUser.role === "staff",
          };

          setUser(updatedUser);
        }
      } catch (error) {
        console.error("Failed to parse user data", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();
  }, []);

  const login = (userData, token = null) => {
    // Determine the token to use
    const userToken = token || userData.token || "";

    // Determine if user is verified based on role
    const isVerified = userData.role === "admin" || userData.role === "staff";

    // Create complete user object
    const completeUser = {
      ...defaultUser,
      ...userData,
      token: userToken,
      isVerified,
      isAuthenticated: true,
      lastLogin: new Date().toISOString(),
    };

    // Update state
    setUser(completeUser);

    // Store in localStorage - save both user data and token
    localStorage.setItem("myToken", completeUser.token);
  };

  const logout = () => {
    // Reset user state to default
    setUser(defaultUser);

    // Clear localStorage
    localStorage.removeItem("myToken");
  };

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user?.id,
    login,
    logout,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
